import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/auth';

interface ProspectingDayRow {
  date: string;
  calls_count: number;
  conversations_count: number;
  rdvs_booked: number;
}

interface AppointmentRow {
  id: string;
  date: string;
  time: string | null;
  prospect_name: string | null;
  company_name: string | null;
  job_title: string | null;
  status: string | null;
  created_at: string | null;
}

export interface NormalizedAppointment extends AppointmentRow {
  status_normalized: string;
}

export interface KpiData {
  totalCalls: number;
  totalConversations: number;
  totalRdvs: number;
  conversionRate: number;
  previousCalls: number;
  previousConversations: number;
  previousRdvs: number;
  previousConversionRate: number;
}

export interface ChartDataPoint {
  label: string;
  conversations: number;
  rdvs: number;
  rdvsCreated: number;
}

export interface CumulativeDataPoint {
  date: string;
  timestamp: number;
  cumulative: number;
  label: string;
}

export interface StatusBreakdown {
  status: string;
  label: string;
  count: number;
  color: string;
}

export interface QualityMetrics {
  realizationRate: number;
  avgDelayDays: number;
  upcomingRdvs: number;
}

export interface ActivityMetrics {
  totalDays: number;
  plannedDays: number;
  callsPerDay: number;
  rdvsPerDay: number;
  reachabilityRate: number;
}

function normalizeStatus(status: string | null): string {
  if (!status) return 'autre';
  const s = status.toLowerCase().trim();
  if (s === 'scheduled' || s === 'planifié' || s === 'planifie') return 'planifie';
  if (s === 'confirmed' || s === 'confirmé' || s === 'confirme') return 'confirme';
  if (s === 'completed' || s === 'effectué' || s === 'effectue' || s === 'showed') return 'effectue';
  if (s === 'cancelled' || s === 'annulé' || s === 'annule') return 'annule';
  if (s === 'postponed' || s === 'reporté' || s === 'reporte' || s === 'rescheduled') return 'reporte';
  if (s.includes('no reply') || s.includes('vm') || s.includes('sms') || s === 'no_reply') return 'no_reply';
  if (s === 'no_show' || s === 'no-show' || s === 'noshow') return 'no_show';
  return 'autre';
}

function getStatusLabel(normalized: string): string {
  const map: Record<string, string> = {
    planifie: 'Planifié',
    effectue: 'Effectué',
    confirme: 'Confirmé',
    reporte: 'Reporté',
    annule: 'Annulé',
    no_reply: 'Pas de réponse',
    no_show: 'No-show',
    autre: 'Autre',
  };
  return map[normalized] || 'Autre';
}

function getStatusColor(normalized: string): string {
  const map: Record<string, string> = {
    planifie: '#3B82F6',
    effectue: '#10B981',
    confirme: '#06B6D4',
    reporte: '#F59E0B',
    annule: '#EF4444',
    no_reply: '#6B7280',
    no_show: '#9F1239',
    autre: '#9CA3AF',
  };
  return map[normalized] || '#9CA3AF';
}

function getPreviousPeriod(start: string, end: string): { prevStart: string; prevEnd: string } {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const durationMs = endDate.getTime() - startDate.getTime();
  const prevEnd = new Date(startDate.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - durationMs);
  return {
    prevStart: prevStart.toISOString().split('T')[0],
    prevEnd: prevEnd.toISOString().split('T')[0],
  };
}

export function useStatistics(clientId: string | null, startDate: string, endDate: string) {
  const [prospectingData, setProspectingData] = useState<ProspectingDayRow[]>([]);
  const [previousProspectingData, setPreviousProspectingData] = useState<ProspectingDayRow[]>([]);
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [previousAppointmentsCount, setPreviousAppointmentsCount] = useState(0);
  const [upcomingAppointments, setUpcomingAppointments] = useState<{ id: string; status: string | null }[]>([]);
  const [plannedDays, setPlannedDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { prevStart, prevEnd } = getPreviousPeriod(startDate, endDate);
      const today = new Date().toISOString().split('T')[0];

      const [prospRes, prevProsRes, apptRes, prevApptRes, upcomingRes, plannedRes] = await Promise.all([
        supabase
          .from('prospecting_days')
          .select('date, calls_count, conversations_count, rdvs_booked')
          .eq('client_id', clientId)
          .gte('date', startDate)
          .lte('date', endDate),

        supabase
          .from('prospecting_days')
          .select('date, calls_count, conversations_count, rdvs_booked')
          .eq('client_id', clientId)
          .gte('date', prevStart)
          .lte('date', prevEnd),

        supabase
          .from('appointments')
          .select('id, date, time, prospect_name, company_name, job_title, status, created_at')
          .eq('client_id', clientId)
          .gte('created_at', startDate)
          .lte('created_at', endDate + 'T23:59:59.999Z')
          .order('created_at', { ascending: false }),

        supabase
          .from('appointments')
          .select('id')
          .eq('client_id', clientId)
          .gte('created_at', prevStart)
          .lte('created_at', prevEnd + 'T23:59:59.999Z'),

        supabase
          .from('appointments')
          .select('id, status')
          .eq('client_id', clientId)
          .gte('date', today),

        supabase
          .from('prospecting_schedule')
          .select('id')
          .eq('client_id', clientId)
          .gte('date', startDate)
          .lte('date', endDate),
      ]);

      if (prospRes.error) throw prospRes.error;
      if (prevProsRes.error) throw prevProsRes.error;
      if (apptRes.error) throw apptRes.error;
      if (prevApptRes.error) throw prevApptRes.error;
      if (upcomingRes.error) throw upcomingRes.error;
      if (plannedRes.error) throw plannedRes.error;

      setProspectingData(prospRes.data || []);
      setPreviousProspectingData(prevProsRes.data || []);
      setAppointments(apptRes.data || []);
      setPreviousAppointmentsCount((prevApptRes.data || []).length);
      setUpcomingAppointments(upcomingRes.data || []);
      setPlannedDays((plannedRes.data || []).length);
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [clientId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpis = useMemo<KpiData>(() => {
    const totalCalls = prospectingData.reduce((s, d) => s + (d.calls_count || 0), 0);
    const totalConversations = prospectingData.reduce((s, d) => s + (d.conversations_count || 0), 0);
    const totalRdvs = appointments.length;
    const conversionRate = totalConversations > 0 ? (totalRdvs / totalConversations) * 100 : 0;

    const previousCalls = previousProspectingData.reduce((s, d) => s + (d.calls_count || 0), 0);
    const previousConversations = previousProspectingData.reduce((s, d) => s + (d.conversations_count || 0), 0);
    const previousRdvs = previousAppointmentsCount;
    const previousConversionRate = previousConversations > 0 ? (previousRdvs / previousConversations) * 100 : 0;

    return {
      totalCalls,
      totalConversations,
      totalRdvs,
      conversionRate,
      previousCalls,
      previousConversations,
      previousRdvs,
      previousConversionRate,
    };
  }, [prospectingData, previousProspectingData, appointments, previousAppointmentsCount]);

  const normalizedAppointments = useMemo<NormalizedAppointment[]>(() => {
    return appointments.map((a) => ({
      ...a,
      status_normalized: normalizeStatus(a.status),
    }));
  }, [appointments]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate).getTime();
    const durationDays = (endMs - startMs) / (1000 * 60 * 60 * 24);
    const useDaily = durationDays <= 7;
    const useWeekly = durationDays > 7 && durationDays <= 31;

    const buckets: Record<string, { conversations: number; rdvs: number; rdvsCreated: number }> = {};

    prospectingData.forEach((d) => {
      const date = new Date(d.date);
      let key: string;

      if (useDaily) {
        key = d.date;
      } else if (useWeekly) {
        const dayOfWeek = date.getDay();
        const monday = new Date(date);
        monday.setDate(date.getDate() - ((dayOfWeek + 6) % 7));
        key = monday.toISOString().split('T')[0];
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!buckets[key]) buckets[key] = { conversations: 0, rdvs: 0, rdvsCreated: 0 };
      buckets[key].conversations += d.conversations_count || 0;
      buckets[key].rdvs += d.rdvs_booked || 0;
      buckets[key].rdvsCreated += d.rdvs_booked || 0;
    });

    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => {
        let label: string;
        const d = new Date(key);

        if (useDaily) {
          const dayNum = d.getDate();
          const months = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
          label = `${dayNum} ${months[d.getMonth()]}`;
        } else if (useWeekly) {
          const dayNum = d.getDate();
          const months = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
          label = `${dayNum} ${months[d.getMonth()]}`;
        } else {
          const [, m] = key.split('-');
          const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
          label = months[parseInt(m) - 1];
        }

        return {
          label,
          conversations: val.conversations,
          rdvs: val.rdvs,
          rdvsCreated: val.rdvsCreated,
        };
      });
  }, [prospectingData, startDate, endDate]);

  const statusBreakdown = useMemo<StatusBreakdown[]>(() => {
    const counts: Record<string, number> = {};
    normalizedAppointments.forEach((a) => {
      if (a.status_normalized === 'planifie') return;
      counts[a.status_normalized] = (counts[a.status_normalized] || 0) + 1;
    });

    const order = ['effectue', 'confirme', 'reporte', 'annule', 'no_reply', 'autre'];
    return order
      .filter((s) => (counts[s] || 0) > 0)
      .map((s) => ({
        status: s,
        label: getStatusLabel(s),
        count: counts[s],
        color: getStatusColor(s),
      }));
  }, [normalizedAppointments]);

  const qualityMetrics = useMemo<QualityMetrics>(() => {
    const completed = normalizedAppointments.filter((a) => a.status_normalized === 'effectue').length;
    const total = normalizedAppointments.length;
    const realizationRate = total > 0 ? (completed / total) * 100 : 0;

    const delays = normalizedAppointments
      .filter((a) => a.created_at && a.date)
      .map((a) => {
        const bookDateStr = a.created_at!.split('T')[0];
        const bookDate = new Date(bookDateStr);
        const rdvDate = new Date(a.date);
        return Math.max(0, (rdvDate.getTime() - bookDate.getTime()) / (1000 * 60 * 60 * 24));
      });
    const avgDelayDays = delays.length > 0 ? Math.round(delays.reduce((s, d) => s + d, 0) / delays.length) : 0;

    const upcomingRdvs = upcomingAppointments.filter((a) => {
      const ns = normalizeStatus(a.status);
      return ns === 'planifie' || ns === 'confirme';
    }).length;

    return { realizationRate, avgDelayDays, upcomingRdvs };
  }, [normalizedAppointments, upcomingAppointments]);

  const recentAppointments = useMemo(() => {
    return normalizedAppointments.slice(0, 10);
  }, [normalizedAppointments]);

  const donutTotal = useMemo(() => {
    return normalizedAppointments.filter((a) => a.status_normalized !== 'planifie').length;
  }, [normalizedAppointments]);

  const activityMetrics = useMemo<ActivityMetrics>(() => {
    const totalDays = prospectingData.length;
    const totalCalls = prospectingData.reduce((s, d) => s + (d.calls_count || 0), 0);
    const totalConversations = prospectingData.reduce((s, d) => s + (d.conversations_count || 0), 0);
    const totalRdvsBooked = prospectingData.reduce((s, d) => s + (d.rdvs_booked || 0), 0);

    const callsPerDay = totalDays > 0 ? totalCalls / totalDays : 0;
    const rdvsPerDay = totalDays > 0 ? totalRdvsBooked / totalDays : 0;
    const reachabilityRate = totalCalls > 0 ? (totalConversations / totalCalls) * 100 : 0;

    return {
      totalDays,
      plannedDays,
      callsPerDay,
      rdvsPerDay,
      reachabilityRate,
    };
  }, [prospectingData, plannedDays]);

  const cumulativeChartData = useMemo<CumulativeDataPoint[]>(() => {
    const sortedAppointments = [...appointments]
      .filter((a) => a.created_at)
      .sort((a, b) => new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime());

    if (sortedAppointments.length === 0) return [];

    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate + 'T23:59:59').getTime();
    const durationDays = (endMs - startMs) / (1000 * 60 * 60 * 24);

    const useDays = durationDays <= 31;
    const useWeeks = durationDays > 31 && durationDays <= 90;

    const dataPoints: CumulativeDataPoint[] = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);
    let cumulativeCount = 0;
    let appointmentIndex = 0;

    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= end) {
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);

      while (
        appointmentIndex < sortedAppointments.length &&
        new Date(sortedAppointments[appointmentIndex].created_at!).getTime() <= dayEnd.getTime()
      ) {
        cumulativeCount++;
        appointmentIndex++;
      }

      let label: string;
      if (useDays) {
        label = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      } else if (useWeeks) {
        const dayOfWeek = currentDate.getDay();
        const monday = new Date(currentDate);
        monday.setDate(currentDate.getDate() - ((dayOfWeek + 6) % 7));
        label = `${String(monday.getDate()).padStart(2, '0')}/${String(monday.getMonth() + 1).padStart(2, '0')}`;
      } else {
        const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
        label = months[currentDate.getMonth()];
      }

      dataPoints.push({
        date: currentDate.toISOString().split('T')[0],
        timestamp: currentDate.getTime(),
        cumulative: cumulativeCount,
        label,
      });

      if (useDays) {
        currentDate.setDate(currentDate.getDate() + 1);
      } else if (useWeeks) {
        currentDate.setDate(currentDate.getDate() + 1);
      } else {
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    if (useWeeks) {
      const weeklyData: Record<string, CumulativeDataPoint> = {};
      dataPoints.forEach((point) => {
        const date = new Date(point.date);
        const dayOfWeek = date.getDay();
        const monday = new Date(date);
        monday.setDate(date.getDate() - ((dayOfWeek + 6) % 7));
        const key = monday.toISOString().split('T')[0];

        if (!weeklyData[key] || point.cumulative > weeklyData[key].cumulative) {
          weeklyData[key] = {
            ...point,
            date: key,
            label: `${String(monday.getDate()).padStart(2, '0')}/${String(monday.getMonth() + 1).padStart(2, '0')}`,
          };
        }
      });
      return Object.values(weeklyData).sort((a, b) => a.timestamp - b.timestamp);
    } else if (!useDays) {
      const monthlyData: Record<string, CumulativeDataPoint> = {};
      dataPoints.forEach((point) => {
        const date = new Date(point.date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[key] || point.cumulative > monthlyData[key].cumulative) {
          monthlyData[key] = point;
        }
      });
      return Object.values(monthlyData).sort((a, b) => a.timestamp - b.timestamp);
    }

    return dataPoints;
  }, [appointments, startDate, endDate]);

  return {
    loading,
    error,
    kpis,
    chartData,
    cumulativeChartData,
    normalizedAppointments,
    statusBreakdown,
    qualityMetrics,
    activityMetrics,
    recentAppointments,
    totalAppointments: donutTotal,
  };
}
